import { useEffect, useRef } from 'react'

interface SheetProps {
  open: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  /** 'bottom' slides up from bottom (mobile-first); 'right' slides in from right (desktop sidebar) */
  side?: 'bottom' | 'right'
  /** Height hint for bottom sheet, default auto up to 90vh */
  snap?: 'half' | 'full' | 'auto'
}

export default function Sheet({ open, onClose, title, children, side = 'bottom', snap = 'auto' }: SheetProps) {
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  const snapHeight = snap === 'half' ? '50vh' : snap === 'full' ? '100vh' : undefined

  return (
    <>
      {/* Backdrop */}
      <div
        ref={overlayRef}
        onClick={e => { if (e.target === overlayRef.current) onClose() }}
        className="fixed inset-0 z-[70]"
        style={{
          background: 'rgba(2,5,16,0.75)',
          backdropFilter: 'blur(4px)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.25s ease',
        }}
      />

      {/* Panel */}
      <div
        className={`
          fixed z-[70] bg-ab-surface border-white border-opacity-8 flex flex-col
          ${side === 'bottom'
            ? 'bottom-0 left-0 right-0 rounded-t-2xl border-t border-l border-r'
            : 'top-0 right-0 bottom-0 w-full max-w-md rounded-l-2xl border-l border-t border-b'
          }
        `}
        style={{
          maxHeight: side === 'bottom' ? snapHeight ?? '90vh' : undefined,
          transform: open
            ? 'translate(0,0)'
            : side === 'bottom' ? 'translateY(100%)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.32,0.72,0,1)',
          boxShadow: '0 -8px 48px rgba(0,0,0,0.5)',
        }}
      >
        {/* Drag handle */}
        {side === 'bottom' && (
          <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-white opacity-20" />
          </div>
        )}

        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-5 py-4 border-b border-white border-opacity-5 flex-shrink-0">
            <h2 className="font-display font-black text-base text-ab-text tracking-tight">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="text-ab-text-dim hover:text-ab-text transition-colors text-lg leading-none bg-transparent border-none cursor-pointer"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        )}

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-5">
          {children}
        </div>
      </div>
    </>
  )
}
